import { app, auth } from "./init";
import { artistHubEnabled } from "./artistHub";
export type WorkspaceRole = "staff" | "artist" | "editor" | "reviewer";
export interface ArtistDraft {
  contentId: string;
  artistId: string;
  type: "post" | "vlog" | "story";
  /** B-17: "fans" = 鐵粉限定; the server still reads the legacy "member" value as fans. */
  audience: "public" | "fans" | "member";
  title: string;
  displayTitle?: string;
  media?: { assetId: string; alt?: string }[];
  coverAssetId?: string | null;
  video?: {assetId: string; status: string; durationMs?: number; width?: number; height?: number} | null;
  body: string;
  status: "draft" | "pending_review" | "rejected" | "published" | "unpublished" | "deleted";
  version: number;
  reviewNote?: string;
  hasPublishedContent?: boolean;
  /** B-05: set when the content was soft-deleted (read-only afterwards). */
  deletedAt?: number | null;
  /** B-19: stories only — publishedAt + 24 h (null until published). */
  expiresAt?: number | null;
}
export type DraftSummary = Omit<ArtistDraft, "body" | "artistId">;
export interface WorkspacePage {
  role: WorkspaceRole;
  canPublish?: boolean;
  items: DraftSummary[];
  nextCursor: string | null;
}
export class WorkspaceError extends Error {
  constructor(public code: string) {
    super(
      (
        {
          PHOTO_REQUIRED: "貼文至少需要 1 張照片，請先在 App 加入照片。",
          CONFLICT:
            "內容已被修改，或狀態已改變。請先複製目前文字，再重新載入；不會自動覆寫。",
          FORBIDDEN: "沒有此藝人的工作區權限，或權限已撤銷。",
          UNAUTHENTICATED: "登入已變更，請重新開啟工作區。",
          NOT_FOUND: "找不到資料，請確認藝人或使用者已存在。",
          INVALID_ARGUMENT: "請檢查必填欄位與字數。",
          FEATURE_NOT_READY: "此帳號尚未開放社群試點，請聯絡營運。",
          MEDIA_NOT_READY:
            "影片、封面或預覽副本尚未處理完成，請稍後重新載入。",
        } as Record<string, string>
      )[code] || "操作未完成，請重試。",
    );
  }
}
export async function workspaceRequest<T>(
  action: string,
  artistId: string,
  input: Record<string, unknown> = {},
): Promise<T> {
  if (!artistHubEnabled) throw new WorkspaceError("FORBIDDEN");
  const user = auth.currentUser;
  if (!user || user.isAnonymous) throw new WorkspaceError("UNAUTHENTICATED");
  try {
    const token = await user.getIdToken();
    const response = await fetch(
      `https://asia-east1-${app.options.projectId}.cloudfunctions.net/artistHub`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...input, action, artistId }),
        signal: AbortSignal.timeout(20000),
      },
    );
    if (auth.currentUser?.uid !== user.uid)
      throw new WorkspaceError("UNAUTHENTICATED");
    const data = await response.json();
    if (auth.currentUser?.uid !== user.uid)
      throw new WorkspaceError("UNAUTHENTICATED");
    if (!response.ok) throw new WorkspaceError(data.error);
    return data as T;
  } catch (error) {
    if (error instanceof WorkspaceError) throw error;
    throw new WorkspaceError("TEMPORARILY_UNAVAILABLE");
  }
}

/* ---------- B-19: 限動回覆 inbox (team only: artist / editor / staff) ---------- */
export interface StoryReply {
  replyId: string;
  contentId: string;
  fromUid: string;
  fromName: string;
  fromAvatarUrl: string | null;
  text: string;
  createdAt: number | null;
}
export const storyReplies = (artistId: string, cursor?: string | null, limit = 20) =>
  workspaceRequest<{ items: StoryReply[]; nextCursor: string | null }>("storyReplies", artistId, {
    limit,
    ...(cursor ? { cursor } : {}),
  });
